import React, { useContext, useEffect, useState } from "react";
import {
  FormControl,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Button,
  Grid,
} from "@material-ui/core";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Alert, Checkbox, FormControlLabel } from "@mui/material";
import { useValidPasswordAdvance } from "../../../hooks/useValidPasswordAdvance";
import { alertStyle, btnStyle, h3Style, inputStyle } from "../styles";
import { AuthContext } from "../../../contexts/authContext";
import SuccessFailedModal from "../../../components/SuccessFailedModal";
import { useAppSelector } from "../../../hooks/reduxHooks";
import { selectCurrentClinician } from "../../../store/ClinicianStore/selectors";
import { ClinicRepository } from "../../../repositories/ClinicRepository";

interface FormValues {
  name: string;
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

export function Security() {
  const authContext = useContext(AuthContext);
  const user = useAppSelector(
    ({ currentClinician }) => currentClinician.currentClinician
  );
  const [twoFactor, setTwoFactor] = useState<boolean>(
    user?.mfaEnabled ?? false
  );
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [confirmPass, setConfirmPass] = useState("");
  const [confirmPassError, setConfirmPassError] = useState("");
  const [showPassword, setshowPassword] = useState<boolean>(false);
  const [showCPassword, setshowCPassword] = useState<boolean>(false);
  const [showCurrentPassword, setshowCurrentPassword] =
    useState<boolean>(false);
  const { passwordAdvance, setPasswordAdvance, passwordAdvanceIsValid } =
    useValidPasswordAdvance(null);
  const [genericErrorMessage, setGenericErrorMessage] = useState("");

  const [isSuccessModalOpen, setIsSuccessModalOpen] = React.useState(false);
  const currentClinician = useAppSelector(selectCurrentClinician);

  const phoneNumber = currentClinician?.profileInfo.phoneNumber?.slice(
    currentClinician?.profileInfo?.phoneNumber?.length - 4,
    currentClinician?.profileInfo?.phoneNumber?.length
  );

  const clinicRepository = ClinicRepository.getInstance();

  useEffect(() => {
    onComparePassword(confirmPass);
  }, [confirmPass, passwordAdvance]);

  const onComparePassword = (confirmPass: string) => {
    setConfirmPass(confirmPass);
    if (confirmPass == "") {
      setConfirmPassError("");
      return;
    }
    if (confirmPass !== passwordAdvance) {
      setConfirmPassError(
        "The passwords you have entered don’t match, please correct to proceed"
      );
    } else {
      setConfirmPassError("Passwords match");
    }
  };

  const handleClickShowPassword = () => {
    setshowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleClickShowCPassword = () => {
    setshowCPassword(!showCPassword);
  };

  const handleMouseDownCPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleClickShowCurrentPassword = () => {
    setshowCurrentPassword(!showCurrentPassword);
  };

  const onchangePassword = async (e: any) => {
    e.stopPropagation();
    try {
      const response = await authContext.changePassword(
        currentPassword,
        passwordAdvance
      );
      if (response !== "SUCCESS") {
        throw new Error(response);
      }
      setIsSuccessModalOpen(true);
    } catch (error: any) {
      setGenericErrorMessage(`An error ocurred: ${error.message}`);
    }
  };

  const checkMFA = async (event: any) => {
    const checked = event.target.checked;
    setTwoFactor(checked);
    if (!checked) {
      try {
        await authContext.disableMFA(currentClinician?.profileInfo.email);
        await clinicRepository.updateCurrentClinician(user?._id ?? "", {
          mfaEnabled: false,
        });
      } catch (error) {
        console.log(error);
      }
    } else {
      try {
        await authContext.enableMFA(currentClinician?.profileInfo.email);
        await clinicRepository.updateCurrentClinician(user?._id ?? "", {
          mfaEnabled: true,
        });
      } catch (error) {
        console.log(error);
      }
    }
  };
  return (
    <>
      <Grid>
        {/*  <Grid xs={12} md={6} lg={6}>
          <h3 style={h3Style}>Two-Factor Authentication</h3>
          <FormControlLabel
            control={
              <Checkbox
                disabled={!phoneNumber}
                checked={twoFactor}
                onChange={checkMFA}
                name="twoFactor"
                color="primary"
                style={{ marginRight: 20, marginLeft: 10 }}
              />
            }
            label={
              phoneNumber
                ? "Allow to receive a verification code to the number ****" +
                  phoneNumber +
                  " at every login to the application"
                : "Click on 'Profile' to add your phone and enable this feature"
            }
          />
        </Grid> */}

        <Grid xs={12} md={6} lg={6} style={{ marginTop: 40 }}>
          <h3 style={h3Style}>Change Password</h3>
          <FormControl fullWidth variant="outlined">
            <OutlinedInput
              style={{ ...inputStyle, marginTop: "12px" }}
              type={showCurrentPassword ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              name="currentPassword"
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowCurrentPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {!showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              placeholder="Enter Current Password"
            />
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <OutlinedInput
              style={{ ...inputStyle, marginTop: "12px" }}
              type={showPassword ? "text" : "password"}
              value={passwordAdvance}
              error={
                passwordAdvanceIsValid[0].text === "Password Strength: Good" ||
                passwordAdvanceIsValid[0].text === ""
                  ? false
                  : true
              }
              onChange={(e) => setPasswordAdvance(e.target.value)}
              name="newPassword"
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {!showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              placeholder="Enter New Password"
            />
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <OutlinedInput
              style={{ ...inputStyle, marginTop: "12px" }}
              type={showCPassword ? "text" : "password"}
              value={confirmPass}
              onChange={(e) => setConfirmPass(e.target.value)}
              name="confirmNewPassword"
              error={
                passwordAdvanceIsValid[0].text === "Password Strength: Good" ||
                passwordAdvanceIsValid[0].text === ""
                  ? false
                  : true
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowCPassword}
                    onMouseDown={handleMouseDownCPassword}
                    edge="end"
                  >
                    {!showCPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              placeholder="Confirm New Password"
            />
          </FormControl>
        </Grid>

        {confirmPassError !== "Passwords match" && confirmPassError !== "" && (
          <Alert style={alertStyle} variant="outlined" severity="error">
            Error: Passwords doesn't match
          </Alert>
        )}
        {!isSuccessModalOpen && (
          <>
            {passwordAdvanceIsValid
              ?.filter((item) => item.text !== "Password Strength: Good")
              .filter((item) => !item.active)
              .filter((item) => item.text)
              .map((item, index) => (
                <Alert style={alertStyle} variant="outlined" severity="error">
                  Error: {item.text}
                </Alert>
              ))}
            {genericErrorMessage != "" ? (
              <Alert style={alertStyle} variant="outlined" severity="error">
                Error: {genericErrorMessage}
              </Alert>
            ) : null}
          </>
        )}
        <Button
          type="submit"
          color="primary"
          variant="contained"
          style={{ ...btnStyle, width: "auto", marginBottom: 20 }}
          fullWidth
          onClick={(e) => {
            onchangePassword(e);
          }}
          disabled={
            !(
              passwordAdvanceIsValid[0].text === "Password Strength: Good" &&
              confirmPassError === "Passwords match" &&
              currentPassword
            )
          }
        >
          Save Changes
        </Button>

        <SuccessFailedModal
          isError={false}
          successMessage="Password changed successfully"
          open={isSuccessModalOpen}
          onClose={() => setIsSuccessModalOpen(false)}
        />
      </Grid>
    </>
  );
}
